<script lang="ts">
	import { createEventDispatcher, onMount, onDestroy, tick } from 'svelte';
	import { format } from 'svelte-i18n';
	import { IconButton } from '@Shelly/shelly-design-system';
	import { devices } from 'src/stores/lists/devices';
import { groups } from 'src/stores/lists/groups';
import { rooms } from 'src/stores/lists/rooms';
	import { scenes } from 'src/stores/lists/scenes';
	import { mobile } from '../../services/mobile-rework';
	import {
		escapeRegExp,
		renderTokens as renderTokensHelper,
		detectAutoTokenizable as detectAutoTokenizableHelper,
		type AutoSuggestion,
	} from './helpers';
	import {
		createSpeechRecognition,
		isSpeechRecognitionSupported,
		type SpeechRecognitionController,
		type SpeechRecognitionError,
	} from './speech-recognition';

	export let value = '';
	export let disabled = false;
	export let placeholder = 'Type your message. Use @ to reference rooms, $ for groups or % for scenes';
	export let sendDisabled = false;
	export let browsingHistory = false;
	export let externalAutocomplete = false;

	let windowWidth = typeof window !== 'undefined' ? window.innerWidth : 1024;
	$: isMobileDevice = mobile.isMobile || windowWidth <= 768;

	const dispatch = createEventDispatcher();

	// Voice recognition state
	let isRecording = false;
	let interimTranscript = '';
	let speechErrorKey: string | null = null;
	let speechController: SpeechRecognitionController | null = null;
	let voiceSupported = false;

	// Initialize speech recognition - called lazily on first use
	function initSpeechRecognition() {
		console.log('[Voice] Checking speech recognition support...');
		voiceSupported = isSpeechRecognitionSupported();
		console.log('[Voice] Speech recognition supported:', voiceSupported);

		if (!voiceSupported) {
			console.warn('[Voice] Speech recognition API not available');
			return false;
		}

		console.log('[Voice] Creating speech controller...');
		speechController = createSpeechRecognition({
			interimResults: true,
			continuous: false,
			onStart: () => {
				console.log('[Voice] Recording started');
				isRecording = true;
				speechErrorKey = null;
				interimTranscript = '';
			},
			onResult: (transcript: string, isFinal: boolean) => {
				console.log('[Voice] Result:', { transcript, isFinal });
				if (isFinal) {
					// Append final transcript to the input value
					const newValue = value ? `${value} ${transcript}` : transcript;
					setValue(newValue);
					interimTranscript = '';
				} else {
					interimTranscript = transcript;
				}
			},
			onError: (error: SpeechRecognitionError) => {
				console.error('[Voice] Error:', error);
				speechErrorKey = error.translationKey;
				isRecording = false;
				interimTranscript = '';
				// Clear error after 5 seconds
				setTimeout(() => {
					speechErrorKey = null;
				}, 5000);
			},
			onEnd: () => {
				console.log('[Voice] Recording ended');
				isRecording = false;
				interimTranscript = '';
			},
		});
		console.log('[Voice] Speech controller created successfully');
		return true;
	}

	async function startRecording() {
		console.log('[Voice] Start recording button clicked');
		console.log('[Voice] Location:', {
			protocol: window.location.protocol,
			hostname: window.location.hostname,
			href: window.location.href,
			isSecureContext: window.isSecureContext,
		});
		console.log('[Voice] Browser:', navigator.userAgent);

		// Check if we're in a secure context using browser's native check
		if (!window.isSecureContext) {
			console.error(
				'[Voice] Not in secure context - microphone access requires HTTPS or localhost',
			);
			console.error('[Voice] Current URL:', window.location.href);
			console.error('[Voice] Solutions:');
			console.error('  1. Enable HTTPS in vite.config.ts: server: { https: true }');
			console.error('  2. Access via https:// instead of http://');
			console.error('  3. Use ngrok/cloudflared tunnel with HTTPS');

			// Show user-friendly error about HTTPS requirement
			speechErrorKey = 'ai_chat_voice_needs_https';
			setTimeout(() => {
				speechErrorKey = null;
			}, 8000); // Longer timeout for this important message
			return;
		}

		console.log('[Voice] Secure context confirmed');

		// Initialize synchronously with user interaction for mobile permission requests
		if (!speechController) {
			console.log('[Voice] Initializing speech controller...');
			const initialized = initSpeechRecognition();
			if (!initialized) {
				console.error('[Voice] Initialization failed');
				speechErrorKey = 'ai_chat_voice_not_supported';
				setTimeout(() => {
					speechErrorKey = null;
				}, 5000);
				return;
			}
		}

		// Start immediately after initialization to maintain user gesture context
		console.log('[Voice] Attempting to start recognition...');
		try {
			speechController?.start();
			console.log('[Voice] Start called successfully');
		} catch (error) {
			console.error('[Voice] Failed to start:', error);
			const errorMsg = error instanceof Error ? error.message : String(error);
			console.error('[Voice] Error details:', errorMsg);
			speechErrorKey = 'ai_chat_microphone_denied';
			setTimeout(() => {
				speechErrorKey = null;
			}, 5000);
		}
	}

	function stopRecording() {
		speechController?.stop();
	}

	function toggleRecording() {
		if (isRecording) {
			stopRecording();
		} else {
			startRecording();
		}
	}

	// Expose toggle function for external keyboard shortcuts
	export function toggleVoiceInput() {
		toggleRecording();
	}

	export function isVoiceSupported() {
		return voiceSupported;
	}

	let editorEl: HTMLDivElement;
	let showDropdown = false;
	let mentionQuery = '';
	let mentionStartIndex = -1;
	let selectedIndex = 0;
	let dropdownElement: HTMLDivElement;
	let triggerType: 'room' | 'device' | 'group' | 'scene' | null = null;
	let contextRoom: string | null = null;

	// Track if we're in the middle of composition (IME input)
	let isComposing = false;

	let autoSuggestion: AutoSuggestion | null = null;

	interface MentionOption {
		type: 'room' | 'device' | 'group' | 'scene';
		id: string | number;
		name: string;
		icon: string;
		roomName?: string;
		roomId?: string | number;
	}

	// Build mention options from stores
	let roomOptions: MentionOption[] = [];
	let deviceOptions: MentionOption[] = [];
	let groupOptions: MentionOption[] = [];
	let sceneOptions: MentionOption[] = [];

	$: roomOptions = Object.values($rooms).map((r: any) => ({
		type: 'room' as const,
		id: r.id,
		name: r.name,
		icon: 'fas fa-door-open',
	}));

	$: deviceOptions = Object.values($devices).map((d: any) => {
		const room = ($rooms as any)[d.room_id];
		return {
			type: 'device' as const,
			id: d.id,
			name: d.name,
			icon: 'fas fa-plug',
			roomName: room?.name || 'Unassigned',
			roomId: d.room_id,
		};
	});

	$: groupOptions = Object.values($groups).map((g: any) => ({
		type: 'group' as const,
		id: g.id,
		name: g.name,
		icon: 'fas fa-layer-group',
	}));

	$: sceneOptions = Object.entries($scenes).map(([id, s]: [string, any]) => ({
		type: 'scene' as const,
		id,
		name: (s as any)?._meta?.name || id,
		icon: 'fas fa-bolt',
	}));

	// Get lists of names for token detection
	$: roomNames = roomOptions.map((r) => r.name);
	$: deviceNames = deviceOptions.map((d) => d.name);
	$: groupNamesList = groupOptions.map((g) => g.name);
	$: sceneNamesList = sceneOptions.map((s) => s.name);

	// Check if query characters appear in order (subsequence match)
	function isSubsequence(name: string, query: string): boolean {
		let qi = 0;
		for (const ch of name) {
			if (ch === query[qi]) qi++;
			if (qi === query.length) return true;
		}
		return false;
	}

	// Helper to check if name matches query and return match score
	// Higher score = better match (exact > starts with > contains > subsequence)
	function getMatchScore(name: string, query: string): number {
		if (!query) return 1; // Empty query matches everything equally
		const lowerName = name.toLowerCase();
		const lowerQuery = query.toLowerCase();

		if (lowerName === lowerQuery) return 100; // Exact match
		if (lowerName.startsWith(lowerQuery)) return 50; // Starts with
		if (lowerName.includes(lowerQuery)) return 10; // Contains substring
		if (isSubsequence(lowerName, lowerQuery)) return 5; // Fuzzy/subsequence match
		return 0; // No match
	}

	// Helper to check if name matches query
	function matchesQuery(opt: MentionOption): boolean {
		return getMatchScore(opt.name, mentionQuery) > 0;
	}

	// Get match indices for highlighting
	function getMatchIndices(name: string, query: string): { start: number; end: number } | null {
		if (!query) return null;
		const lowerName = name.toLowerCase();
		const lowerQuery = query.toLowerCase();
		const index = lowerName.indexOf(lowerQuery);
		if (index === -1) return null;
		return { start: index, end: index + query.length };
	}

	// Extract room context from text before cursor (find last @RoomName token)
	function extractRoomContext(text: string): string | null {
		let lastRoom: string | null = null;

		for (const room of roomOptions) {
			const pattern = new RegExp(`@${escapeRegExp(room.name)}(?:\\s|$)`, 'g');
			let match;
			while ((match = pattern.exec(text)) !== null) {
				lastRoom = room.name;
			}
		}

		return lastRoom;
	}

	// Filter and sort options based on trigger type and query
	$: filteredOptions = (() => {
		if (triggerType === 'room') {
			return roomOptions
				.filter(matchesQuery)
				.sort(
					(a, b) => getMatchScore(b.name, mentionQuery) - getMatchScore(a.name, mentionQuery),
				)
				.slice(0, 15);
		} else if (triggerType === 'device') {
			const matching = deviceOptions
				.filter(matchesQuery)
				.sort(
					(a, b) => getMatchScore(b.name, mentionQuery) - getMatchScore(a.name, mentionQuery),
				);

			if (contextRoom) {
				const inRoom = matching.filter((d) => d.roomName === contextRoom);
				const notInRoom = matching.filter((d) => d.roomName !== contextRoom);
				return [...inRoom, ...notInRoom].slice(0, 15);
			}

			return matching.slice(0, 15);
		} else if (triggerType === 'group') {
			return groupOptions
				.filter(matchesQuery)
				.sort(
					(a, b) => getMatchScore(b.name, mentionQuery) - getMatchScore(a.name, mentionQuery),
				)
				.slice(0, 15);
		} else if (triggerType === 'scene') {
			return sceneOptions
				.filter(matchesQuery)
				.sort(
					(a, b) => getMatchScore(b.name, mentionQuery) - getMatchScore(a.name, mentionQuery),
				)
				.slice(0, 15);
		}
		return [];
	})();

	// Auto-scroll to best match when filtered options change
	$: if (showDropdown && filteredOptions.length > 0 && mentionQuery) {
		// Find best match index
		let bestIndex = 0;
		let bestScore = 0;
		filteredOptions.forEach((opt, i) => {
			const score = getMatchScore(opt.name, mentionQuery);
			if (score > bestScore) {
				bestScore = score;
				bestIndex = i;
			}
		});
		selectedIndex = bestIndex;
		// Scroll to selected after DOM update
		tick().then(() => scrollToSelected());
	}

	// Group devices by whether they're in context room
	$: groupedDevices = (() => {
		if (triggerType !== 'device') return { inRoom: [], other: [] };

		const devices = filteredOptions as MentionOption[];
		if (contextRoom) {
			return {
				inRoom: devices.filter((d) => d.roomName === contextRoom),
				other: devices.filter((d) => d.roomName !== contextRoom),
			};
		}
		return { inRoom: [], other: devices };
	})();

	// Render text with styled tokens using shared helper
	function renderTokens(text: string): string {
		return renderTokensHelper(text, roomNames, deviceNames, {
			classPrefix: 'ai-mention-token',
			addEditableAttributes: true,
			groupNames: groupNamesList,
			sceneNames: sceneNamesList,
		});
	}

	// Get cursor position in text (character offset)
	function getCursorPosition(): number {
		const selection = window.getSelection();
		if (!selection || selection.rangeCount === 0 || !editorEl) return 0;

		const range = selection.getRangeAt(0);
		const preCaretRange = range.cloneRange();
		preCaretRange.selectNodeContents(editorEl);
		preCaretRange.setEnd(range.endContainer, range.endOffset);

		return preCaretRange.toString().length;
	}

	// Restore cursor position after innerHTML update
	function restoreCursorPosition(position: number) {
		if (!editorEl) return;

		const range = document.createRange();
		const sel = window.getSelection();
		if (!sel) return;

		let currentPos = 0;
		let lastNode: Node | null = null;
		let lastNodeEndPos = 0;

		// Walk through all text nodes (including those inside token spans)
		const walker = document.createTreeWalker(editorEl, NodeFilter.SHOW_TEXT);

		while (walker.nextNode()) {
			const node = walker.currentNode as Text;
			const nodeLength = node.length;

			// Check if this text node is inside a token span (non-editable)
			const isInsideToken = node.parentElement?.classList.contains('ai-mention-token');

			if (isInsideToken) {
				// For tokens, treat them as atomic - position is either before or after
				if (currentPos + nodeLength >= position) {
					// Position is within or at the end of this token
					// Place cursor after the token span
					const tokenSpan = node.parentElement;
					if (tokenSpan && tokenSpan.nextSibling) {
						if (tokenSpan.nextSibling.nodeType === Node.TEXT_NODE) {
							range.setStart(tokenSpan.nextSibling, 0);
						} else {
							range.setStartAfter(tokenSpan);
						}
					} else if (tokenSpan) {
						range.setStartAfter(tokenSpan);
					}
					range.collapse(true);
					sel.removeAllRanges();
					sel.addRange(range);
					return;
				}
				currentPos += nodeLength;
			} else {
				// Regular text node
				if (currentPos + nodeLength >= position) {
					const offset = Math.min(position - currentPos, nodeLength);
					range.setStart(node, offset);
					range.collapse(true);
					sel.removeAllRanges();
					sel.addRange(range);
					return;
				}
				currentPos += nodeLength;
			}

			lastNode = node;
			lastNodeEndPos = currentPos;
		}

		// If we couldn't find the position, put cursor at the end
		range.selectNodeContents(editorEl);
		range.collapse(false);
		sel.removeAllRanges();
		sel.addRange(range);
	}

	// Detect plain text that could be tokenized using shared helper
	function detectAutoTokenizable(text: string, cursorPos: number): AutoSuggestion | null {
		return detectAutoTokenizableHelper(
			text,
			cursorPos,
			roomOptions,
			deviceOptions,
			groupOptions,
			sceneOptions,
		);
	}

	function handleInput() {
		if (isComposing || !editorEl) return;

		const rawText = editorEl.innerText || '';
		const text = rawText.trim();
		const cursorPos = getCursorPosition();

		if (!text) {
			editorEl.innerHTML = '';
			value = '';
		} else {
			value = rawText;
		}

		// Check for @, #, $, % triggers
		const textBeforeCursor = text.slice(0, cursorPos);
		const triggerChars = [
			{ char: '@', type: 'room' as const },
			{ char: '#', type: 'device' as const },
			{ char: '$', type: 'group' as const },
			{ char: '%', type: 'scene' as const },
		];

		let newTriggerIndex = -1;
		let newTriggerType: 'room' | 'device' | 'group' | 'scene' | null = null;
		const allTriggerChars = triggerChars.map((t) => t.char);

		for (const { char, type } of triggerChars) {
			const lastIdx = textBeforeCursor.lastIndexOf(char);
			if (lastIdx === -1) continue;

			const textAfter = textBeforeCursor.slice(lastIdx + 1);
			const hasOtherTrigger = allTriggerChars.some((c) => c !== char && textAfter.includes(c));
			if (textAfter.includes(' ') || hasOtherTrigger) continue;

			if (lastIdx > newTriggerIndex) {
				newTriggerIndex = lastIdx;
				newTriggerType = type;
			}
		}

		if (newTriggerType !== null && newTriggerIndex !== -1) {
			mentionStartIndex = newTriggerIndex;
			mentionQuery = textBeforeCursor.slice(newTriggerIndex + 1);
			triggerType = newTriggerType;
			showDropdown = true;
			selectedIndex = 0;
			autoSuggestion = null;

			if (newTriggerType === 'device') {
				contextRoom = extractRoomContext(textBeforeCursor.slice(0, newTriggerIndex));
			} else {
				contextRoom = null;
			}

			return;
		}

		// No active trigger - check for auto-tokenization and render tokens
		showDropdown = false;
		triggerType = null;
		contextRoom = null;

		// Detect if current word matches a room/device name
		autoSuggestion = detectAutoTokenizable(text, cursorPos);

		// Re-render with tokens (after dropdown check to avoid flicker)
		updateRenderedContent(text, cursorPos);
	}

	async function updateRenderedContent(text: string, cursorPos: number) {
		const newHtml = renderTokens(text);
		const currentHtml = editorEl.innerHTML;

		// Only update if content changed (to avoid cursor jumping)
		if (currentHtml !== newHtml) {
			editorEl.innerHTML = newHtml;
			await tick();
			restoreCursorPosition(cursorPos);
		}
	}

	function handleKeyDown(e: KeyboardEvent) {
		// Handle Tab for auto-tokenization
		if (e.key === 'Tab' && autoSuggestion && !showDropdown) {
			e.preventDefault();
			applyAutoToken(autoSuggestion);
			return;
		}

		if (!showDropdown) {
			if (e.key === 'Enter' && !e.shiftKey) {
				e.preventDefault();
				dispatch('enter');
			} else if (e.key === 'Enter' && e.shiftKey) {
				// Allow Shift+Enter for line breaks (browser default behavior)
				return;
			} else if (e.key === 'ArrowUp' && (value.trim() === '' || browsingHistory)) {
				e.preventDefault();
				dispatch('historyUp');
			} else if (e.key === 'ArrowDown' && browsingHistory) {
				e.preventDefault();
				dispatch('historyDown');
			}
			return;
		}

		switch (e.key) {
			case 'ArrowDown':
				e.preventDefault();
				selectedIndex = Math.min(selectedIndex + 1, filteredOptions.length - 1);
				scrollToSelected();
				break;
			case 'ArrowUp':
				e.preventDefault();
				selectedIndex = Math.max(selectedIndex - 1, 0);
				scrollToSelected();
				break;
			case 'Enter':
				e.preventDefault();
				if (filteredOptions[selectedIndex]) {
					selectMention(filteredOptions[selectedIndex]);
				}
				break;
			case 'Escape':
				e.preventDefault();
				showDropdown = false;
				break;
			case 'Tab':
				if (filteredOptions[selectedIndex]) {
					e.preventDefault();
					selectMention(filteredOptions[selectedIndex]);
				}
				break;
		}
	}

	function scrollToSelected() {
		if (dropdownElement) {
			const selectedEl = dropdownElement.querySelector('.mention-option.selected');
			if (selectedEl) {
				selectedEl.scrollIntoView({ block: 'nearest' });
			}
		}
	}

	const symbolForType: Record<MentionOption['type'], string> = {
		room: '@',
		device: '#',
		group: '$',
		scene: '%',
	};

	async function selectMention(option: MentionOption) {
		const text = value;
		const before = text.slice(0, mentionStartIndex);
		const cursorPos = getCursorPosition();
		const after = text.slice(cursorPos);

		const symbol = symbolForType[option.type];
		const newText = `${before}${symbol}${option.name} ${after}`;

		// Prevent reactive statement from interfering
		isUpdatingProgrammatically = true;

		value = newText;
		showDropdown = false;
		triggerType = null;
		contextRoom = null;

		// Update the editor content with tokens
		// New cursor position: after the symbol + name + space
		const newCursorPos = mentionStartIndex + 1 + option.name.length + 1; // symbol + name + space
		editorEl.innerHTML = renderTokens(newText);

		await tick();
		restoreCursorPosition(newCursorPos);
		editorEl.focus();

		isUpdatingProgrammatically = false;
	}

	async function applyAutoToken(suggestion: AutoSuggestion) {
		const before = value.slice(0, suggestion.startIndex);
		const after = value.slice(suggestion.endIndex);
		const symbol = symbolForType[suggestion.type];

		const newText = `${before}${symbol}${suggestion.text} ${after}`;

		// Prevent reactive statement from interfering
		isUpdatingProgrammatically = true;

		value = newText;
		autoSuggestion = null;

		// New cursor position: startIndex + symbol + name + space
		const newCursorPos = suggestion.startIndex + 1 + suggestion.text.length + 1;
		editorEl.innerHTML = renderTokens(newText);

		await tick();
		restoreCursorPosition(newCursorPos);
		editorEl.focus();

		isUpdatingProgrammatically = false;
	}

	function handleClickOutside(e: MouseEvent) {
		if (dropdownElement && !dropdownElement.contains(e.target as Node)) {
			showDropdown = false;
		}
	}

	function handleFocus() {
		dispatch('focus');
	}

	function handleBlur() {
		setTimeout(() => {
			if (!dropdownElement?.contains(document.activeElement)) {
				showDropdown = false;
			}
		}, 150);
		dispatch('blur');
	}

	function handlePaste(e: ClipboardEvent) {
		e.preventDefault();
		const text = e.clipboardData?.getData('text/plain') || '';

		// Insert plain text at cursor position
		const selection = window.getSelection();
		if (!selection || selection.rangeCount === 0) return;

		const range = selection.getRangeAt(0);
		range.deleteContents();

		const textNode = document.createTextNode(text);
		range.insertNode(textNode);

		// Move cursor to end of inserted text
		range.setStartAfter(textNode);
		range.collapse(true);
		selection.removeAllRanges();
		selection.addRange(range);

		// Trigger input handling
		handleInput();
	}

	export function focus() {
		editorEl?.focus();
	}

	export function clear() {
		value = '';
		if (editorEl) {
			editorEl.innerHTML = '';
		}
	}

	export function setValue(newValue: string) {
		isUpdatingProgrammatically = true;
		value = newValue;
		if (editorEl) {
			editorEl.innerHTML = renderTokens(newValue);
			// Place cursor at the end
			const range = document.createRange();
			const sel = window.getSelection();
			if (sel) {
				range.selectNodeContents(editorEl);
				range.collapse(false);
				sel.removeAllRanges();
				sel.addRange(range);
			}
		}
		isUpdatingProgrammatically = false;
	}

	export function selectMentionExternal(option: MentionOption) {
		selectMention(option);
	}

	// Flag to prevent reactive statement from interfering during programmatic updates
	let isUpdatingProgrammatically = false;

	// Dispatch mentionchange event when autocomplete state changes
	$: dispatch('mentionchange', {
		active: showDropdown,
		triggerType,
		mentionQuery,
		filteredOptions,
	});

	// Sync external value changes to editor (only when not updating programmatically)
	$: if (editorEl && value !== editorEl.innerText && !isUpdatingProgrammatically) {
		const cursorPos = getCursorPosition();
		editorEl.innerHTML = renderTokens(value);
		if (document.activeElement === editorEl) {
			restoreCursorPosition(cursorPos);
		}
	}

	// Clear suggestion state when value is cleared externally (e.g. on send)
	$: if (!value) {
		autoSuggestion = null;
		showDropdown = false;
	}

	function handleResize() {
		windowWidth = window.innerWidth;
	}

	onMount(() => {
		if (editorEl && value) {
			editorEl.innerHTML = renderTokens(value);
		}
		// Check voice support but don't initialize yet - wait for user interaction
		voiceSupported = isSpeechRecognitionSupported();
		window.addEventListener('resize', handleResize);
	});

	onDestroy(() => {
		if (isRecording) {
			stopRecording();
		}
		window.removeEventListener('resize', handleResize);
	});

	// Handle keyboard shortcuts for voice input
	function handleGlobalKeyDown(e: KeyboardEvent) {
		// Ctrl+Shift+Space to toggle voice input
		if (e.ctrlKey && e.shiftKey && e.code === 'Space') {
			e.preventDefault();
			toggleRecording();
		}
	}
</script>

<svelte:window on:click={handleClickOutside} on:keydown={handleGlobalKeyDown} />

<div class="rich-mention-input-wrapper">
	<div class="input-container">
		<div
			bind:this={editorEl}
			class="rich-mention-input"
			class:disabled
			class:recording={isRecording}
			contenteditable={!disabled}
			role="textbox"
			tabindex="-1"
			aria-label="Chat message input"
			aria-autocomplete="list"
			aria-haspopup="listbox"
			aria-controls={showDropdown ? 'mention-dropdown' : undefined}
			data-placeholder={placeholder}
			on:input={handleInput}
			on:keydown={handleKeyDown}
			on:focus={handleFocus}
			on:blur={handleBlur}
			on:paste={handlePaste}
			on:compositionstart={() => (isComposing = true)}
			on:compositionend={() => {
				isComposing = false;
				handleInput();
			}}
		></div>
		<div class="input-toolbar">
			<div class="toolbar-left">
				<slot name="toolbar-left" />
			</div>
			<div class="toolbar-right">
				<slot name="toolbar-right" />
				{#if isRecording}
					<IconButton
						variant="primary"
						icon="fas fa-stop"
						on:click={stopRecording}
						aria-label={$format('ai_chat_stop_recording_aria_label', {
							default: 'Stop recording',
						})}
						title={$format('ai_chat_stop_recording_title', { default: 'Stop recording' })}
						class={sendDisabled ? 'send-button disabled' : 'send-button paper-plane-button'}
					/>
				{:else if value.trim() === '' && voiceSupported}
					<IconButton
						variant="secondary"
						icon="fa-regular fa-microphone"
						on:click={startRecording}
						{disabled}
						aria-label={$format('ai_chat_start_voice_input_aria_label', {
							default: 'Start voice input',
						})}
						title={$format('ai_chat_voice_input_title', {
							default: 'Voice input (Ctrl+Shift+Space)',
						})}
						class="send-button voice-button"
					/>
				{:else}
					<IconButton
						variant="primary"
						icon="fa-regular fa-paper-plane"
						on:click={() => dispatch('enter')}
						disabled={sendDisabled}
						aria-label="Send message"
						title={$format('ai_chat_send_message_title', { default: 'Send' })}
						class={sendDisabled ? 'send-button disabled' : 'send-button paper-plane-button'}
					/>
				{/if}
			</div>
		</div>
	</div>

	<!-- Voice recording status -->
	{#if isRecording}
		<div class="voice-status recording-status" role="status" aria-live="polite">
			<span class="recording-indicator"></span>
			<span class="status-text">{$format('ai_chat_listening', { default: 'Listening...' })}</span>
			{#if interimTranscript}
				<span class="interim-transcript">"{interimTranscript}"</span>
			{/if}
		</div>
	{/if}

	<!-- Voice error message -->
	{#if speechErrorKey}
		<div class="voice-status error-status" role="alert">
			<i class="fas fa-exclamation-circle" aria-hidden="true"></i>
			<span>{$format(speechErrorKey, { default: 'An error occurred with voice input' })}</span>
		</div>
	{/if}

	<!-- Auto-tokenization suggestion -->
	{#if autoSuggestion && !showDropdown}
		{#if isMobileDevice}
			<button
				class="auto-suggest-hint auto-suggest-hint--mobile"
				on:click={() => applyAutoToken(autoSuggestion)}
				type="button"
			>
				{$format('ai_chat_tap_to_use', { default: 'Tap to use' })}
				<span class="token-preview {autoSuggestion.type}">
					{symbolForType[autoSuggestion.type]}{autoSuggestion.text}
				</span>
			</button>
		{:else}
			<div class="auto-suggest-hint" role="status" aria-live="polite">
				<kbd>Tab</kbd> to convert "{autoSuggestion.text}" to
				<span class="token-preview {autoSuggestion.type}">
					{symbolForType[autoSuggestion.type]}{autoSuggestion.text}
				</span>
			</div>
		{/if}
	{/if}

	{#if showDropdown && filteredOptions.length > 0 && !externalAutocomplete}
		<div
			bind:this={dropdownElement}
			id="mention-dropdown"
			class="mention-dropdown"
			role="listbox"
			aria-label="{triggerType} suggestions"
		>
			<!-- Context hint for devices -->
			{#if triggerType === 'device' && contextRoom}
				<div class="context-hint">
					<i class="fas fa-filter" aria-hidden="true"></i>
					Showing devices in <strong>{contextRoom}</strong> first
				</div>
			{/if}

			{#if triggerType === 'room'}
				<!-- Rooms list -->
				<div class="mention-group">
					<div class="mention-group-label room-label">
						<i class="fas fa-door-open" aria-hidden="true"></i>
						Rooms
						<span class="trigger-hint">@</span>
					</div>
					{#each filteredOptions as option, i}
						{@const matchIndices = getMatchIndices(option.name, mentionQuery)}
						<button
							class="mention-option"
							class:selected={selectedIndex === i}
							role="option"
							aria-selected={selectedIndex === i}
							on:click={() => selectMention(option)}
							on:mouseenter={() => (selectedIndex = i)}
						>
							<i class={option.icon} aria-hidden="true"></i>
							<span class="option-name">
								{#if matchIndices}
									{option.name.slice(0, matchIndices.start)}<mark
										class="match-highlight"
										>{option.name.slice(matchIndices.start, matchIndices.end)}</mark
									>{option.name.slice(matchIndices.end)}
								{:else}
									{option.name}
								{/if}
							</span>
						</button>
					{/each}
				</div>
			{:else if triggerType === 'group'}
				<!-- Groups list -->
				<div class="mention-group">
					<div class="mention-group-label group-label">
						<i class="fas fa-layer-group" aria-hidden="true"></i>
						Groups
						<span class="trigger-hint">$</span>
					</div>
					{#each filteredOptions as option, i}
						{@const matchIndices = getMatchIndices(option.name, mentionQuery)}
						<button
							class="mention-option"
							class:selected={selectedIndex === i}
							role="option"
							aria-selected={selectedIndex === i}
							on:click={() => selectMention(option)}
							on:mouseenter={() => (selectedIndex = i)}
						>
							<i class={option.icon} aria-hidden="true"></i>
							<span class="option-name">
								{#if matchIndices}
									{option.name.slice(0, matchIndices.start)}<mark
										class="match-highlight"
										>{option.name.slice(matchIndices.start, matchIndices.end)}</mark
									>{option.name.slice(matchIndices.end)}
								{:else}
									{option.name}
								{/if}
							</span>
						</button>
					{/each}
				</div>
			{:else if triggerType === 'scene'}
				<!-- Scenes list -->
				<div class="mention-group">
					<div class="mention-group-label scene-label">
						<i class="fas fa-bolt" aria-hidden="true"></i>
						Scenes
						<span class="trigger-hint">%</span>
					</div>
					{#each filteredOptions as option, i}
						{@const matchIndices = getMatchIndices(option.name, mentionQuery)}
						<button
							class="mention-option"
							class:selected={selectedIndex === i}
							role="option"
							aria-selected={selectedIndex === i}
							on:click={() => selectMention(option)}
							on:mouseenter={() => (selectedIndex = i)}
						>
							<i class={option.icon} aria-hidden="true"></i>
							<span class="option-name">
								{#if matchIndices}
									{option.name.slice(0, matchIndices.start)}<mark
										class="match-highlight"
										>{option.name.slice(matchIndices.start, matchIndices.end)}</mark
									>{option.name.slice(matchIndices.end)}
								{:else}
									{option.name}
								{/if}
							</span>
						</button>
					{/each}
				</div>
			{:else if triggerType === 'device'}
				<!-- Devices list - grouped by room context -->
				{#if groupedDevices.inRoom.length > 0}
					<div class="mention-group in-room-group">
						<div class="mention-group-label device-label">
							<i class="fas fa-plug" aria-hidden="true"></i>
							In {contextRoom}
							<span class="trigger-hint">#</span>
						</div>
						{#each groupedDevices.inRoom as option}
							{@const globalIndex = filteredOptions.indexOf(option)}
							{@const matchIndices = getMatchIndices(option.name, mentionQuery)}
							<button
								class="mention-option"
								class:selected={selectedIndex === globalIndex}
								role="option"
								aria-selected={selectedIndex === globalIndex}
								on:click={() => selectMention(option)}
								on:mouseenter={() => (selectedIndex = globalIndex)}
							>
								<i class={option.icon} aria-hidden="true"></i>
								<span class="option-name">
									{#if matchIndices}
										{option.name.slice(0, matchIndices.start)}<mark
											class="match-highlight"
											>{option.name.slice(
												matchIndices.start,
												matchIndices.end,
											)}</mark
										>{option.name.slice(matchIndices.end)}
									{:else}
										{option.name}
									{/if}
								</span>
							</button>
						{/each}
					</div>
				{/if}

				{#if groupedDevices.other.length > 0}
					<div class="mention-group">
						<div class="mention-group-label device-label">
							<i class="fas fa-plug" aria-hidden="true"></i>
							{contextRoom ? 'Other Devices' : 'Devices'}
							<span class="trigger-hint">#</span>
						</div>
						{#each groupedDevices.other as option}
							{@const globalIndex = filteredOptions.indexOf(option)}
							{@const matchIndices = getMatchIndices(option.name, mentionQuery)}
							<button
								class="mention-option"
								class:selected={selectedIndex === globalIndex}
								role="option"
								aria-selected={selectedIndex === globalIndex}
								on:click={() => selectMention(option)}
								on:mouseenter={() => (selectedIndex = globalIndex)}
							>
								<i class={option.icon} aria-hidden="true"></i>
								<span class="option-name">
									{#if matchIndices}
										{option.name.slice(0, matchIndices.start)}<mark
											class="match-highlight"
											>{option.name.slice(
												matchIndices.start,
												matchIndices.end,
											)}</mark
										>{option.name.slice(matchIndices.end)}
									{:else}
										{option.name}
									{/if}
								</span>
								{#if option.roomName}
									<span class="option-room">{option.roomName}</span>
								{/if}
							</button>
						{/each}
					</div>
				{/if}
			{/if}
		</div>
	{/if}

	{#if showDropdown && filteredOptions.length === 0 && mentionQuery.length > 0}
		<div class="mention-dropdown mention-empty">
			<span>
				No {triggerType === 'room'
					? 'rooms'
					: triggerType === 'device'
						? 'devices'
						: triggerType === 'group'
							? 'groups'
							: 'scenes'} found for "{mentionQuery}"
			</span>
		</div>
	{/if}
</div>

<style>
	.rich-mention-input-wrapper {
		position: relative;
		width: 100%;
	}

	.input-container {
		display: flex;
		flex-direction: column;
		gap: 12px;
		border: 1px solid #161d2d;
		border-radius: 12px;
		background: #0e1013;
		padding: 12px;
		transition:
			border-color 0.2s,
			box-shadow 0.2s;
	}

	.input-container:focus-within {
		border-color: #243040;
		box-shadow: 0 0 0 2px rgba(42, 63, 95, 0.3);
	}

	.rich-mention-input {
		--input-max-lines: 5;
		width: 100%;
		min-height: 20px;
		max-height: calc(var(--input-max-lines) * 20px + 12px);
		overflow-y: auto;
		padding: 0;
		border: none;
		border-radius: 0;
		background: transparent;
		color: var(--color-text-primary, #eaeaea);
		font-family: 'Inter', sans-serif;
		font-size: 13px;
		font-style: normal;
		font-weight: 400;
		line-height: 20px;
		outline: none;
		white-space: pre-wrap;
		word-wrap: break-word;
	}

	.rich-mention-input:not(:empty) {
		font-style: normal;
	}

	.input-toolbar {
		display: flex;
		align-items: center;
		justify-content: space-between;
		gap: 10px;
	}

	.toolbar-left {
		display: flex;
		align-items: center;
		gap: 8px;
	}

	.toolbar-right {
		display: flex;
		align-items: center;
		gap: 4px;
		margin-left: auto;
	}

	/*
	 * :global() required for IconButton components passed with class props.
	 * These buttons are child components whose internal elements cannot be
	 * styled with Svelte's scoped styles. Scoped to .toolbar-right to prevent leakage.
	 */
	.toolbar-right :global(.send-button) {
		width: 32px;
		height: 32px;
		background: var(--color-background-2, #242527) !important;
		border-radius: 8px !important;
		border: none !important;
	}

	.toolbar-right :global(.send-button:hover:not(:disabled)) {
		background: var(--color-background-2, #242527) !important;
	}

	.toolbar-right :global(.send-button:disabled) {
		opacity: 0.5;
	}

	.toolbar-right :global(.voice-button) {
		background: transparent !important;
		border: none !important;
		box-shadow: none !important;
		outline: none !important;
	}

	.toolbar-right :global(.voice-button:hover),
	.toolbar-right :global(.voice-button:focus),
	.toolbar-right :global(.voice-button:hover:not(:disabled)) {
		background: var(--color-background-2, #242527) !important;
		border: none !important;
		box-shadow: none !important;
		outline: none !important;
	}

	.toolbar-right :global(.paper-plane-button) {
		background: var(--color-background-2, #242527) !important;
		border: none !important;
		box-shadow: none !important;
	}

	.toolbar-right :global(.paper-plane-button:hover:not(:disabled)) {
		background: var(--color-background-2, #242527) !important;
		border: none !important;
		box-shadow: none !important;
	}

	.toolbar-right :global(.paper-plane-button:disabled) {
		opacity: 0.3;
		background: var(--color-background-2, #242527) !important;
	}

	.rich-mention-input.disabled {
		opacity: 0.6;
		cursor: not-allowed;
		pointer-events: none;
	}

	/* Placeholder styling */
	.rich-mention-input:empty::before {
		content: attr(data-placeholder);
		color: var(--color-text-muted, #a1a2a2);
		font-style: normal;
		pointer-events: none;
	}

	/* Token styles are now in TokenStyles.svelte */

	/* Auto-suggestion hint */
	.auto-suggest-hint {
		position: absolute;
		bottom: 100%;
		left: 0;
		right: 0;
		padding: 8px 12px;
		background: var(--color-background-3, #2a2a2a);
		border: 1px solid var(--color-border, #3a3a3a);
		border-radius: 8px;
		margin-bottom: 4px;
		font-size: 12px;
		color: var(--color-text-secondary, #888);
		display: flex;
		align-items: center;
		gap: 6px;
		flex-wrap: wrap;
	}

	.auto-suggest-hint--mobile {
		cursor: pointer;
		background: var(--color-background-3, #2a2a2a);
		border: 1px solid var(--color-primary-1, #4a9eff);
		font: inherit;
		padding: 10px 14px;
	}

	.auto-suggest-hint--mobile:active {
		background: var(--color-background-4, #3a3a3a);
	}

	.auto-suggest-hint kbd {
		display: inline-block;
		padding: 2px 6px;
		background: var(--color-background-4, #3a3a3a);
		border: 1px solid var(--color-border, #555);
		border-radius: 4px;
		font-family: monospace;
		font-size: 11px;
		color: var(--color-text-primary, #fff);
	}

	/* Token preview styles are now in TokenStyles.svelte */

	/* Context hint for devices */
	.context-hint {
		display: flex;
		align-items: center;
		gap: 6px;
		padding: 8px 12px;
		background: linear-gradient(135deg, rgba(76, 175, 80, 0.1) 0%, transparent 100%);
		border-bottom: 1px solid var(--color-border, #3a3a3a);
		font-size: 12px;
		color: var(--color-success, #4caf50);
	}

	.context-hint i {
		font-size: 10px;
	}

	.context-hint strong {
		font-weight: 600;
	}

	.mention-dropdown {
		position: absolute;
		bottom: 100%;
		left: 0;
		right: 0;
		max-height: 320px;
		overflow-y: auto;
		background: var(--color-background-3, #2a2a2a);
		border: 1px solid var(--color-border, #3a3a3a);
		border-radius: 8px;
		margin-bottom: 4px;
		box-shadow: 0 -4px 12px rgba(0, 0, 0, 0.3);
		z-index: 1000;
	}

	.mention-empty {
		padding: 12px 16px;
		color: var(--color-text-secondary, #888);
		font-size: 13px;
		text-align: center;
	}

	.mention-group {
		padding: 4px 0;
	}

	.mention-group:not(:last-child) {
		border-bottom: 1px solid var(--color-border, #3a3a3a);
	}

	.in-room-group {
		background: rgba(76, 175, 80, 0.05);
	}

	.mention-group-label {
		display: flex;
		align-items: center;
		gap: 8px;
		padding: 8px 12px 4px;
		font-size: 11px;
		font-weight: 600;
		text-transform: uppercase;
		color: var(--color-text-secondary, #888);
		letter-spacing: 0.5px;
	}

	.room-label {
		color: var(--color-primary-1);
	}

	.device-label {
		color: #24d38c;
	}

	.group-label {
		color: var(--color-warning, #ffc107);
	}

	.scene-label {
		color: #9c27b0;
	}

	.trigger-hint {
		margin-left: auto;
		padding: 1px 5px;
		background: var(--color-background-4, #3a3a3a);
		border-radius: 3px;
		font-size: 10px;
		font-weight: 700;
		font-family: monospace;
	}

	.mention-option {
		display: flex;
		align-items: center;
		gap: 10px;
		width: 100%;
		padding: 10px 12px;
		background: transparent;
		border: none;
		color: var(--color-text-primary, #ffffff);
		font-size: 14px;
		text-align: left;
		cursor: pointer;
		transition: background-color 0.15s;
	}

	.mention-option:hover,
	.mention-option.selected {
		background: var(--color-background-4, #3a3a3a);
	}

	.mention-option i {
		color: var(--color-text-secondary, #888);
		width: 16px;
		text-align: center;
	}

	.option-name {
		flex: 1;
		overflow: hidden;
		text-overflow: ellipsis;
		white-space: nowrap;
	}

	/* :global() required for match highlighting rendered via innerHTML */
	.option-name :global(.match-highlight) {
		background: rgba(var(--color-primary-1-rgb, 0, 123, 255), 0.3);
		color: var(--color-primary-1);
		border-radius: 2px;
		padding: 0 2px;
		font-weight: 600;
	}

	.option-room {
		font-size: 12px;
		color: var(--color-text-secondary, #888);
		flex-shrink: 0;
	}

	/* Scrollbar styling */
	.mention-dropdown::-webkit-scrollbar,
	.rich-mention-input::-webkit-scrollbar {
		width: 6px;
	}

	.mention-dropdown::-webkit-scrollbar-track,
	.rich-mention-input::-webkit-scrollbar-track {
		background: transparent;
	}

	.mention-dropdown::-webkit-scrollbar-thumb,
	.rich-mention-input::-webkit-scrollbar-thumb {
		background: var(--color-border, #3a3a3a);
		border-radius: 3px;
	}

	.mention-dropdown::-webkit-scrollbar-thumb:hover,
	.rich-mention-input::-webkit-scrollbar-thumb:hover {
		background: var(--color-text-secondary, #888);
	}

	/* Voice recording styles */
	.rich-mention-input.recording {
		border-color: var(--color-danger, #dc3545);
		box-shadow: 0 0 0 2px rgba(220, 53, 69, 0.2);
	}

	/* :global() required for mic/recording button components passed with class props */
	.input-container :global(.recording-button) {
		background: linear-gradient(135deg, #dc3545, #ff6b6b) !important;
		animation: pulse 1.5s infinite;
	}

	.input-container :global(.mic-button) {
		background: var(--color-primary-1) !important;
	}

	.input-container :global(.mic-button:hover) {
		transform: scale(1.05);
	}

	@keyframes pulse {
		0%,
		100% {
			transform: scale(1);
			box-shadow: 0 0 0 0 rgba(220, 53, 69, 0.4);
		}
		50% {
			transform: scale(1.05);
			box-shadow: 0 0 0 8px rgba(220, 53, 69, 0);
		}
	}

	/* Voice status display */
	.voice-status {
		display: flex;
		align-items: center;
		gap: 8px;
		padding: 8px 12px;
		border-radius: 8px;
		margin-top: 8px;
		font-size: 13px;
	}

	.recording-status {
		background: rgba(220, 53, 69, 0.1);
		border: 1px solid rgba(220, 53, 69, 0.3);
		color: var(--color-danger, #dc3545);
	}

	.error-status {
		background: rgba(220, 53, 69, 0.1);
		border: 1px solid rgba(220, 53, 69, 0.3);
		color: var(--color-danger, #dc3545);
	}

	.recording-indicator {
		width: 10px;
		height: 10px;
		background: var(--color-danger, #dc3545);
		border-radius: 50%;
		animation: blink 1s infinite;
	}

	@keyframes blink {
		0%,
		100% {
			opacity: 1;
		}
		50% {
			opacity: 0.3;
		}
	}

	.status-text {
		font-weight: 500;
	}

	.interim-transcript {
		color: var(--color-text-secondary, #888);
		font-style: italic;
		overflow: hidden;
		text-overflow: ellipsis;
		white-space: nowrap;
		max-width: 200px;
	}
</style>
